// Shared display rules. These describe recorded figures, not spendable cash.
export const treasuryAmount = (value) => {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && !value.trim()) return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
};

export const sumTreasuryAmounts = (values) => {
  const amounts = values.map(treasuryAmount);
  if (amounts.some((amount) => amount === null)) return null;
  return treasuryAmount(amounts.reduce((sum, amount) => sum + amount, 0));
};

export const subtractTreasuryAmounts = (left, right) => {
  const a = treasuryAmount(left);
  const b = treasuryAmount(right);
  return a === null || b === null ? null : treasuryAmount(a - b);
};

export const treasuryUnit = (fund = {}) => {
  if (typeof fund === 'string') fund = { currency: fund };
  if (!fund || typeof fund !== 'object') fund = {};
  const currency = String(fund.currency || '').trim().toUpperCase();
  const tokenSymbol = String(fund.tokenSymbol || '').trim().toUpperCase();
  const crypto = fund.assetType === 'crypto' || Boolean(tokenSymbol);
  const symbol = crypto ? tokenSymbol || String(fund.displaySymbol || currency).trim().toUpperCase() : currency;
  const chain = crypto ? String(fund.chain || '').trim().toLowerCase() : '';
  return {
    key: JSON.stringify([crypto ? 'crypto' : 'fiat', symbol, chain]),
    currency: symbol,
    assetType: crypto ? 'crypto' : 'fiat',
    tokenSymbol: crypto ? symbol : '',
    chain,
    label: symbol ? `${symbol}${chain ? ` · ${chain}` : ''}` : 'Currency unavailable',
  };
};

export const formatTreasuryAmount = (value, fund) => {
  const amount = treasuryAmount(value);
  const unit = treasuryUnit(fund);
  if (amount === null || !unit.currency) return 'Unavailable';
  if (unit.assetType === 'crypto') {
    return `${unit.label} ${amount.toLocaleString('en-AU', { maximumFractionDigits: 8 })}`;
  }
  try {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency', currency: unit.currency, currencyDisplay: 'code',
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${unit.label} ${amount.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
};

export const treasuryDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
};

const displayDate = (value) => new Date(value).toLocaleString('en-AU', {
  day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
});

export const formatTreasuryAsOf = (value) => {
  const date = treasuryDate(value);
  return date ? `As of ${displayDate(date)}` : 'Balance date unavailable';
};

export const groupTreasuryBalances = (funds = []) => {
  const groups = new Map();
  for (const fund of funds) {
    const unit = treasuryUnit(fund);
    if (!groups.has(unit.key)) groups.set(unit.key, { ...unit, funds: [] });
    groups.get(unit.key).funds.push(fund);
  }
  return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label)).map((group) => {
    const dates = group.funds.map((fund) => treasuryDate(fund.lastUpdated ?? fund.balanceUpdatedAt));
    const knownDates = dates.filter(Boolean).sort();
    const balance = group.currency ? sumTreasuryAmounts(group.funds.map((fund) => fund.balance)) : null;
    let dateLabel = 'Balance dates unavailable';
    if (knownDates.length === dates.length && knownDates.length) {
      const first = knownDates[0];
      const last = knownDates.at(-1);
      dateLabel = first === last ? formatTreasuryAsOf(first)
        : `Balance dates: ${displayDate(first)} – ${displayDate(last)}`;
    } else if (knownDates.length) {
      dateLabel = 'Some balance dates unavailable';
    }
    return { ...group, balance, dateLabel };
  });
};
